import { settings } from '@/lib/db';
import { isTauri } from '@/lib/platform';
import { sourceFetch } from '@/lib/platform/fetch';

import type { FolderPermission, SyncFolder, SyncFolderFile } from './folder';

/**
 * iCloud as a sync target, through CloudKit — the one way a web app on an
 * iPhone can reach iCloud on its own.
 *
 * Why not the iCloud Drive folder the desktop app can write: an iOS web app
 * cannot hold a folder (Safari has no File System Access API), and iCloud
 * Drive has no web API for another company's app. CloudKit is Apple's web
 * API for an app's own data in the user's iCloud account. So on this target
 * a device's `.fdv` is a CloudKit record in the pilot's **private database**,
 * one record per device, with the archive as an asset — the same files the
 * folder target writes, in a different place. They are not visible in iCloud
 * Drive; they count against the pilot's iCloud storage.
 *
 * Every device that wants to meet the iPhone syncs here too: the desktop app
 * and a desktop browser can sync to a folder and to iCloud at once, which is
 * what joins them. `docs/SYNC.md` → iCloud.
 *
 * **Signing in.** CloudKit's sign-in is Apple's page, reached by a redirect
 * and answered with a web auth token on the way back. CloudKit JS's own
 * button opens a popup, which a Home Screen app on iOS cannot get an answer
 * back from; so this module fetches the redirect URL itself and leaves by a
 * full-page navigation (web), or opens it in a separate native window whose
 * navigation the shell watches for the token (desktop, `src-tauri`). The
 * token is kept by `authTokenStore`, in IndexedDB. Apple issues it for 30
 * minutes, or two weeks when "Keep me signed in" is ticked, so the pilot is
 * asked to sign in again about once a fortnight — Apple's limit, not ours.
 *
 * **What is not verified.** CloudKit JS is loaded from Apple's CDN at run
 * time and could not be fetched from the environment this was written in, so
 * this module is written against its published interface and tested against
 * a fake; the first real run is on a configured container.
 *
 * Configuration is build time and public: `VITE_CLOUDKIT_CONTAINER`,
 * `VITE_CLOUDKIT_API_TOKEN`, `VITE_CLOUDKIT_ENVIRONMENT`. A build without
 * them offers no iCloud option.
 */

const CLOUDKIT_JS = 'https://cdn.apple-cloudkit.com/ck/2/cloudkit.js';
const API = 'https://api.apple-cloudkit.com/database/1';
export const RECORD_TYPE = 'SyncFile';

/** `sync.` keys: device-local, never in a backup or sync file. */
export const ICLOUD_KEYS = {
  enabled: 'sync.icloud.enabled',
  token: 'sync.icloud.token',
} as const;

export interface ICloudConfig {
  container: string;
  apiToken: string;
  environment: 'development' | 'production';
}

export function icloudConfig(): ICloudConfig | undefined {
  const env = import.meta.env;
  const container = (env.VITE_CLOUDKIT_CONTAINER as string | undefined)?.trim();
  const apiToken = (env.VITE_CLOUDKIT_API_TOKEN as string | undefined)?.trim();
  if (!container || !apiToken) {
    return undefined;
  }
  return {
    container,
    apiToken,
    environment:
      (env.VITE_CLOUDKIT_ENVIRONMENT as string | undefined) === 'development'
        ? 'development'
        : 'production',
  };
}

export function icloudAvailable(): boolean {
  return icloudConfig() !== undefined;
}

/** Raised when CloudKit needs the pilot to sign in (again). */
export class ICloudAuthError extends Error {}

/*
 * The slice of CloudKit JS used here. Its own type package is out of date
 * and unmaintained; these are the calls, as Apple documents them.
 */
export interface CKRecord {
  recordName: string;
  recordType?: string;
  recordChangeTag?: string;
  modified?: { timestamp: number };
  fields: Record<string, { value: unknown } | undefined>;
}
interface CKResponse {
  records: CKRecord[];
  hasErrors?: boolean;
  errors?: { ckErrorCode?: string; serverErrorCode?: string; reason?: string }[];
  moreComing?: boolean;
}
export interface CKDatabase {
  performQuery: (
    query: { recordType: string } | CKResponse,
    options?: { desiredKeys?: string[] },
  ) => Promise<CKResponse>;
  fetchRecords: (names: string[], options?: { desiredKeys?: string[] }) => Promise<CKResponse>;
  saveRecords: (records: unknown[]) => Promise<CKResponse>;
  deleteRecords: (names: string[]) => Promise<CKResponse>;
}
export interface CKContainer {
  setUpAuth: () => Promise<unknown>;
  privateCloudDatabase: CKDatabase;
}
interface CloudKitGlobal {
  configure: (config: unknown) => void;
  getDefaultContainer: () => CKContainer;
}

/*
 * The token, held in memory for CloudKit JS's synchronous `getToken` and
 * written through to IndexedDB. Every CloudKit response may rotate it
 * (`putToken`); the stored copy follows.
 */
let tokenInMemory: string | null = null;

function isAuthError(error: unknown): boolean {
  const e = error as { ckErrorCode?: string; serverErrorCode?: string } | null;
  const code = e?.ckErrorCode ?? e?.serverErrorCode;
  return code === 'AUTHENTICATION_REQUIRED' || code === 'AUTHENTICATION_FAILED';
}

let loading: Promise<CKContainer> | null = null;

function loadScript(): Promise<CloudKitGlobal> {
  const existing = (globalThis as { CloudKit?: CloudKitGlobal }).CloudKit;
  if (existing !== undefined) {
    return Promise.resolve(existing);
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = CLOUDKIT_JS;
    script.async = true;
    script.onload = () => {
      const loaded = (globalThis as { CloudKit?: CloudKitGlobal }).CloudKit;
      if (loaded === undefined) {
        reject(new Error('iCloud could not be reached.'));
      } else {
        resolve(loaded);
      }
    };
    script.onerror = () => {
      reject(new Error('iCloud could not be reached.'));
    };
    document.head.append(script);
  });
}

/** CloudKit JS, loaded once and configured with this app's container and token store. */
export async function cloudKitContainer(config = icloudConfig()): Promise<CKContainer> {
  if (config === undefined) {
    throw new Error('iCloud is not configured in this build.');
  }
  loading ??= (async () => {
    tokenInMemory = await settings.get<string | null>(ICLOUD_KEYS.token, null);
    const CloudKit = await loadScript();
    CloudKit.configure({
      containers: [
        {
          containerIdentifier: config.container,
          apiTokenAuth: { apiToken: config.apiToken, persist: false },
          environment: config.environment,
        },
      ],
      services: {
        authTokenStore: {
          getToken: () => tokenInMemory,
          putToken: (_container: string, token: string | null) => {
            tokenInMemory = token;
            void settings.set(ICLOUD_KEYS.token, token);
          },
        },
        // Native HTTP in the desktop app, whose origin CloudKit would not accept.
        ...(isTauri() ? { fetch: sourceFetch } : {}),
      },
    });
    return CloudKit.getDefaultContainer();
  })().catch((error: unknown) => {
    loading = null;
    throw error;
  });
  return loading;
}

/** Where Apple sends the pilot back with a token: the Settings page. Must match the API token's sign-in callback. */
export function callbackUrl(): string {
  return `${window.location.origin}${import.meta.env.BASE_URL}settings`;
}

/**
 * Apple's sign-in page for this container. CloudKit answers an unauthenticated
 * request with `AUTHENTICATION_REQUIRED` and the address to send the user to.
 */
export async function signInUrl(
  config = icloudConfig(),
  fetchImpl: typeof globalThis.fetch = sourceFetch,
): Promise<string> {
  if (config === undefined) {
    throw new Error('iCloud is not configured in this build.');
  }
  const url = `${API}/${config.container}/${config.environment}/private/users/current?ckAPIToken=${encodeURIComponent(config.apiToken)}`;
  const response = await fetchImpl(url);
  const body = (await response.json().catch(() => ({}))) as { redirectURL?: string };
  if (body.redirectURL === undefined) {
    throw new Error('iCloud sign-in is unavailable.');
  }
  return body.redirectURL;
}

/** The token from a sign-in callback address, under either name CloudKit has used for it. */
export function tokenFromUrl(url: URL): string | null {
  return url.searchParams.get('ckWebAuthToken') ?? url.searchParams.get('ckSession');
}

async function acceptToken(token: string): Promise<void> {
  tokenInMemory = token;
  await settings.set(ICLOUD_KEYS.token, token);
  await settings.set(ICLOUD_KEYS.enabled, true);
}

/**
 * Sends the pilot to Apple's sign-in. In a browser the page leaves and
 * `completeICloudSignIn` finishes on the way back; in the desktop app a
 * separate window opens and the token comes back from the shell.
 *
 * @returns whether the pilot is now signed in (always `false` in a browser,
 *   which is navigating away).
 */
export async function startICloudSignIn(): Promise<boolean> {
  const url = await signInUrl();
  if (isTauri()) {
    const { invoke } = await import('@tauri-apps/api/core');
    const token = await invoke<string | null>('icloud_sign_in', { url });
    if (token === null) {
      return false;
    }
    await acceptToken(token);
    return true;
  }
  window.location.assign(url);
  return false;
}

/**
 * Finishes a browser sign-in if this page load is Apple redirecting back.
 *
 * @returns whether a sign-in was completed.
 */
export async function completeICloudSignIn(
  location: URL = new URL(window.location.href),
): Promise<boolean> {
  const token = tokenFromUrl(location);
  if (token === null || !icloudAvailable()) {
    return false;
  }
  await acceptToken(token);
  if (typeof window !== 'undefined' && window.location.href === location.href) {
    const clean = new URL(location.href);
    clean.searchParams.delete('ckWebAuthToken');
    clean.searchParams.delete('ckSession');
    window.history.replaceState(window.history.state, '', clean.href);
  }
  return true;
}

export async function disconnectICloud(): Promise<void> {
  tokenInMemory = null;
  await settings.set(ICLOUD_KEYS.token, null);
  await settings.set(ICLOUD_KEYS.enabled, false);
}

export async function icloudEnabled(): Promise<boolean> {
  return icloudAvailable() && (await settings.get(ICLOUD_KEYS.enabled, false));
}

/**
 * A record name for a device file. Filenames carry spaces and brackets, which
 * CloudKit does not accept in a record name; base64url keeps them one-to-one.
 */
export function recordNameFor(filename: string): string {
  let binary = '';
  for (const byte of new TextEncoder().encode(filename)) {
    binary += String.fromCharCode(byte);
  }
  return `f-${btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')}`;
}

function check(response: CKResponse, allow: readonly string[] = []): CKResponse {
  const failure = (response.errors ?? []).find(
    (error) => !allow.includes(error.serverErrorCode ?? error.ckErrorCode ?? ''),
  );
  if (response.hasErrors === true && failure !== undefined) {
    if (isAuthError(failure)) {
      throw new ICloudAuthError('iCloud sign-in has expired.');
    }
    throw new Error(
      `iCloud refused the request (${failure.reason ?? failure.serverErrorCode ?? 'unknown'}).`,
    );
  }
  return response;
}

async function guarded<T>(work: () => Promise<T>): Promise<T> {
  try {
    return await work();
  } catch (error) {
    if (error instanceof ICloudAuthError || isAuthError(error)) {
      throw new ICloudAuthError('iCloud sign-in has expired.');
    }
    throw error;
  }
}

export interface ICloudDeps {
  container?: () => Promise<CKContainer>;
  fetchImpl?: typeof globalThis.fetch;
}

/** The private database's `SyncFile` records, as a `SyncFolder`. */
export function icloudFolder(deps: ICloudDeps = {}): SyncFolder {
  const container = deps.container ?? (() => cloudKitContainer());
  const fetchImpl = deps.fetchImpl ?? sourceFetch;
  const database = async () => (await container()).privateCloudDatabase;

  return {
    label: 'iCloud',
    permission: async (): Promise<FolderPermission> => {
      if ((await settings.get<string | null>(ICLOUD_KEYS.token, null)) === null) {
        return 'prompt';
      }
      try {
        return (await (await container()).setUpAuth()) === null ? 'prompt' : 'granted';
      } catch (error) {
        if (isAuthError(error)) {
          return 'prompt';
        }
        throw error;
      }
    },
    requestPermission: () => startICloudSignIn(),
    list: () =>
      guarded(async () => {
        const db = await database();
        const files: SyncFolderFile[] = [];
        let page = check(
          await db.performQuery({ recordType: RECORD_TYPE }, { desiredKeys: ['name', 'size'] }),
        );
        for (;;) {
          for (const record of page.records) {
            const name = record.fields.name?.value;
            if (typeof name === 'string') {
              const size = record.fields.size?.value;
              files.push({
                name,
                lastModified: record.modified?.timestamp ?? 0,
                size: typeof size === 'number' ? size : 0,
              });
            }
          }
          if (page.moreComing !== true) {
            return files;
          }
          // CloudKit JS continues a query from its own previous response.
          page = check(await db.performQuery(page));
        }
      }),
    read: (name) =>
      guarded(async () => {
        const db = await database();
        const response = check(
          await db.fetchRecords([recordNameFor(name)], { desiredKeys: ['archive'] }),
        );
        const asset = response.records[0]?.fields.archive?.value as
          { downloadURL?: string } | undefined;
        if (asset?.downloadURL === undefined) {
          throw new Error(`${name} has no data in iCloud.`);
        }
        // Asset URLs can carry a `${f}` placeholder for the filename.
        const url = asset.downloadURL.replace('${f}', encodeURIComponent(name));
        const download = await fetchImpl(url);
        if (!download.ok) {
          throw new Error(`${name} could not be downloaded from iCloud.`);
        }
        return new Uint8Array(await download.arrayBuffer());
      }),
    write: (name, bytes) =>
      guarded(async () => {
        const db = await database();
        const recordName = recordNameFor(name);
        // A record already there is replaced, which CloudKit only allows with
        // its current change tag. `NOT_FOUND` is the first write.
        const existing = check(await db.fetchRecords([recordName], { desiredKeys: [] }), [
          'NOT_FOUND',
        ]).records[0];
        check(
          await db.saveRecords([
            {
              recordType: RECORD_TYPE,
              recordName,
              ...(existing?.recordChangeTag === undefined
                ? {}
                : { recordChangeTag: existing.recordChangeTag }),
              fields: {
                name: { value: name },
                size: { value: bytes.byteLength },
                archive: { value: new Blob([bytes], { type: 'application/zip' }) },
              },
            },
          ]),
        );
      }),
    remove: (name) =>
      guarded(async () => {
        const db = await database();
        check(await db.deleteRecords([recordNameFor(name)]), ['NOT_FOUND']);
      }),
  };
}
